import React from "react";
import { FaTimes, FaCamera, FaEye } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import { useData } from "../../context/DataContext";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { resolveMediaUrl } from "../../utils/mediaUrl";

function DetailPerBangunan({
  isDetailModalOpen,
  closeDetailModal,
  selectedItem,
  getStatusBadge,
  getStatusIcon,
  openDetailModal,
  id,
  showTrigger = true,
}) {
  const{gambar}=useData()

  const bangunanFotoUrl = (file) =>
    resolveMediaUrl(`${gambar}bangunan/`, file);

  // Framer Motion variants untuk animasi detail modal
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.2, ease: "easeIn" }
    }
  };
  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20 
    },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { 
        duration: 0.4, 
        ease: [0.25, 0.46, 0.45, 0.94] // Smooth easing
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      y: 20,
      transition: { 
        duration: 0.3, 
        ease: "easeIn" 
      }
    }
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { delay: 0.1, duration: 0.3 }
    }
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { delay: 0.2, duration: 0.3 }
    }
  };

  const closeButtonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { delay: 0.1, duration: 0.3 }
    },
    hover: { 
      scale: 1.1,
      rotate: 90,
      transition: { duration: 0.2 }
    }
  };

  return (
    <>
      {showTrigger && (
        <motion.button
          type="button"
          onClick={() => openDetailModal(id)}
          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-all duration-200"
          title="Lihat Detail"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <FaEye />
        </motion.button>
      )}

      {/* ==================== DETAIL MODAL ==================== */}
      <AnimatePresence>
        {isDetailModalOpen && selectedItem && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-900/40 p-4"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={closeDetailModal}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                <motion.div 
                  className="flex items-center justify-between"
                  variants={headerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  <h3 className="text-xl font-semibold text-gray-900">
                    Detail Perawatan Bangunan
                  </h3>
                  <motion.button
                    onClick={closeDetailModal}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
                    variants={closeButtonVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                  >
                    <FaTimes />
                  </motion.button>
                </motion.div>
              </div>

              {/* Content */}
              <motion.div 
                className="p-6 space-y-6"
                variants={contentVariants}
                initial="hidden"
                animate="visible"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3, duration: 0.3 }}
                    >
                      <label className="block text-sm font-medium text-gray-500">
                        Bagian
                      </label>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedItem.nama_bagian || selectedItem.bagian || "-"}
                      </p>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                    >
                      <label className="block text-sm font-medium text-gray-500">
                        Jenis Kerusakan
                      </label>
                      <p className="text-wrap text-gray-900">
                        {selectedItem.jenis_kerusakan || "-"}
                      </p>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5, duration: 0.3 }}
                    >
                      <label className="block text-sm font-medium text-gray-500">
                        Tindakan
                      </label>
                      <p className="text-wrap text-gray-900">
                        {selectedItem.tindakan || "-"}
                      </p>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6, duration: 0.3 }}
                    >
                      <label className="block text-sm font-medium text-gray-500">
                        Petugas
                      </label>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedItem.pic || selectedItem.petugas || "-"}
                      </p>
                    </motion.div>
                  </div>

                  <div className="space-y-4">
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3, duration: 0.3 }}
                    >
                      <label className="block text-sm font-medium text-gray-500">
                        Tanggal Masuk
                      </label>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedItem.tgl_masuk && dayjs(selectedItem.tgl_masuk).isValid()
                          ? dayjs(selectedItem.tgl_masuk).format("DD/MM/YYYY")
                          : "-"}
                      </p>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4, duration: 0.3 }}
                    >
                      <label className="block text-sm font-medium text-gray-500">
                        Tanggal Selesai
                      </label>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedItem.tgl_selesai && dayjs(selectedItem.tgl_selesai).isValid()
                          ? dayjs(selectedItem.tgl_selesai).format("DD/MM/YYYY")
                          : "-"}
                      </p>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5, duration: 0.3 }}
                    >
                      <label className="block text-sm font-medium text-gray-500">
                        Status
                      </label>
                      <span className={getStatusBadge(selectedItem.status)}>
                        {getStatusIcon(selectedItem.status)}
                        {selectedItem.status || "-"}
                      </span>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6, duration: 0.3 }}
                    >
                      <label className="block text-sm font-medium text-gray-500">
                        Biaya
                      </label>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedItem.biaya 
                          ? `Rp ${parseInt(selectedItem.biaya).toLocaleString('id-ID')}`
                          : "-"}
                      </p>
                    </motion.div>
                  </div>
                </div>

                {/* Deskripsi */}
                {(selectedItem.desc || selectedItem.deskripsi) && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.3 }}
                  >
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      Deskripsi
                    </label>
                    <p className="text-gray-900 bg-gray-50 p-4 rounded-lg">
                      {selectedItem.desc || selectedItem.deskripsi}
                    </p>
                  </motion.div>
                )}

                {/* Keterangan */}
                {selectedItem.ket && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.3 }}
                  >
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      Keterangan
                    </label>
                    <p className="text-gray-900 bg-gray-50 p-4 rounded-lg">
                      {selectedItem.ket}
                    </p>
                  </motion.div>
                )}

                {/* Foto Section */}
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9, duration: 0.3 }}
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      Foto Sebelum
                    </label>
                    <motion.div 
                      className="border border-gray-200 rounded-lg p-4 text-center"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      {bangunanFotoUrl(selectedItem.foto_sebelum || selectedItem.befoto) ? (
                        <PhotoProvider>
                          <PhotoView src={bangunanFotoUrl(selectedItem.foto_sebelum || selectedItem.befoto)}>
                            <img
                          src={bangunanFotoUrl(selectedItem.foto_sebelum || selectedItem.befoto)}
                          alt="Foto Sebelum"
                          className="w-full h-48 object-cover rounded-lg cursor-pointer"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                          </PhotoView>
                        </PhotoProvider>
                        
                      ) : (
                        <div className="h-48 flex items-center justify-center text-gray-400">
                          <FaCamera className="h-12 w-12" />
                          <p className="ml-2">Tidak ada foto</p>
                        </div>
                      )}
                    </motion.div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      Foto Sesudah
                    </label>
                    <motion.div 
                      className="border border-gray-200 rounded-lg p-4 text-center"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      {bangunanFotoUrl(selectedItem.foto_sesudah || selectedItem.lasfoto) ? (
                        <PhotoProvider>
                          <PhotoView src={bangunanFotoUrl(selectedItem.foto_sesudah || selectedItem.lasfoto)}>
                          <img
                          src={bangunanFotoUrl(selectedItem.foto_sesudah || selectedItem.lasfoto)}
                          alt="Foto Sesudah"
                          className="w-full h-48 object-cover rounded-lg cursor-pointer"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                          </PhotoView>
                        </PhotoProvider>
                        
                      ) : (
                        <div className="h-48 flex items-center justify-center text-gray-400">
                          <FaCamera className="h-12 w-12" />
                          <p className="ml-2">Tidak ada foto</p>
                        </div>
                      )}
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default DetailPerBangunan;